<template>
  <div :class="$attrs.class">
    <label
      v-if="label"
      :class="{ 'error-text': error || !!errorMessage }"
      :for="$attrs.id"
      v-html="label"
    />
    <v-menu v-bind="{ ...selectMenuPropsDefault, ...menuProps, disabled }" @focus="handleFocus">
      <template #activator="{ on, attrs, value: isOpen }">
        <div
          v-bind="{ ...attrs, role: undefined }"
          :id="$attrs.id"
          :class="{
            'cursor-pointer': !disabled,
            disabled: disabled,
            error: errorMessage || error,
            valid: !disabled && !errorMessage && !error && valid
          }"
          :tabindex="disabled ? -1 : tabindex"
          class="ul-select rounded px-4 d-flex v-input__slot"
          data-cy="ulSelect"
          :style="hasBorder ? `border: 1px solid ${getColor('grey')};` : ''"
          v-on="{
            ...on,
            focus: event => (!disabled ? $emit('focus', event) : () => {}),
            focusin: event => (!disabled ? $emit('focusin', event) : () => {})
          }"
        >
          <slot v-if="valueCmp" name="selection" :item="valueCmp">
            <span class="text-truncate" v-html="valueCmp[itemText]" />
          </slot>
          <span v-else class="ul-select__placeholder">{{ placeholder }}</span>
          <v-spacer :class="{ 'px-1': paddedSpacer }" />
          <v-icon color="black" :class="{ 'rotate-180': isOpen }">
            {{ appendIcon }}
          </v-icon>
        </div>
      </template>
      <v-list>
        <v-list-item
          v-for="(option, index) in options"
          :key="index"
          data-cy="ulSelectListItem"
          class="px-2"
          @click="valueCmp = option"
        >
          <slot name="optionPrepend" :item="option" />
          <v-list-item-content class="black--text">
            <v-list-item-title class="d-flex">
              <slot name="optionTitle" :item="option">
                <span class="text-truncate" v-html="option[itemText]" />
              </slot>
              <template v-if="option === valueCmp">
                <v-spacer />
                <v-icon color="success"> $tick</v-icon>
              </template>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
    <div
      v-if="!hideDetails || (hideDetails === 'auto' && (!!hint || !!errorMessage))"
      class="ul-select__details"
    >
      <div class="error--text" :class="{ 'd-none': !error && !errorMessage }">
        <slot name="errorMessage" :message="errorMessage">
          <span v-html="errorMessage" />
        </slot>
      </div>
      <div class="grey-darken-1--text" :class="{ 'd-none': !hint }">
        <slot name="hint" :message="hint">
          <span v-html="hint" />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import colors from '~/assets/js/colors.js';

export default {
  name: 'UlSelect',
  inheritAttrs: false,
  props: {
    appendIcon: {
      type: String,
      default: '$dropSmall'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      required: false
    },
    hideDetails: {
      type: [String, Boolean],
      default: false
    },
    hint: {
      type: String,
      required: false
    },
    itemText: {
      type: String,
      default: 'value'
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    label: {
      type: String,
      required: false
    },
    menuProps: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Array,
      required: true
    },
    paddedSpacer: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: 0
    },
    valid: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Number, String, Object],
      required: false
    },
    hasBorder: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  computed: {
    valueCmp: {
      get() {
        const incomingValue = this.returnObject ? this.value[this.itemValue] : this.value;
        return this.options.find(opt => opt[this.itemValue] === incomingValue);
      },
      set(newValue) {
        const emittedValue = this.returnObject ? newValue : newValue[this.itemValue];
        this.$emit('input', emittedValue);
        if (emittedValue !== this.value) {
          this.$nextTick(() => {
            this.$emit('change', emittedValue);
          });
        }
      }
    }
  },
  methods: {
    getColor(color) {
      return colors[color];
    }
  }
};
</script>

<style scoped lang="scss">
.ul-select {
  user-select: none;
  font-size: $font-size-root;
  flex: 1 1 auto;
  max-width: 100%;
  padding-top: 10.5px;
  padding-bottom: 10.5px;

  :not(&.disabled) {
    &:hover {
      border-color: $grey-darken;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      border-color: $primary;
    }
  }

  &.disabled {
    background-color: $grey;
    color: rgba($black, 0.38);
  }

  &.error {
    background-color: $white !important;
  }

  &.valid {
    border-color: $success !important;
  }

  &__placeholder {
    color: $grey-darken;
  }

  &__details {
    font-size: $font-size-smaller;
    line-height: $line-height-smaller1;

    & > div {
      margin-top: 6px;
    }
  }
}
</style>
